import { useEffect, useState } from 'react';

import { Button } from '@/components/atoms';
import {
  useSites,
  useUpsertWorkflowConfig,
  useWorkflowConfig,
} from '@/hooks/useConfig';
import { ApiError } from '@/services/api';

import styles from './WorkflowConfigOverlay.module.css';

interface OverlayFormState {
  display_name: string;
  primary_wp_site: string; // UUID or empty
  default_category: string;
  schedule_label: string;
  search_window_hours: string;
  source_url: string;
  enabled: boolean;
}

const EMPTY_FORM: OverlayFormState = {
  display_name: '',
  primary_wp_site: '',
  default_category: '',
  schedule_label: '',
  search_window_hours: '',
  source_url: '',
  enabled: true,
};

interface Props {
  workflowId: string;
  workflowName: string;
}

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
    return `${e.status} ${e.message}`;
  }
  return String(e);
}

export function WorkflowConfigOverlay({ workflowId, workflowName }: Props) {
  const { data: overlay, isLoading } = useWorkflowConfig(workflowId);
  const { data: sites = [] } = useSites(true);
  const upsertMut = useUpsertWorkflowConfig();

  const [form, setForm] = useState<OverlayFormState>(EMPTY_FORM);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (overlay) {
      setForm({
        display_name: overlay.display_name ?? '',
        primary_wp_site: overlay.primary_wp_site ?? '',
        default_category:
          overlay.default_category !== null && overlay.default_category !== undefined
            ? String(overlay.default_category)
            : '',
        schedule_label: overlay.schedule_label ?? '',
        search_window_hours:
          overlay.search_window_hours !== null &&
          overlay.search_window_hours !== undefined
            ? String(overlay.search_window_hours)
            : '',
        source_url: overlay.source_url ?? '',
        enabled: overlay.enabled,
      });
    }
  }, [overlay]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      await upsertMut.mutateAsync({
        workflowId,
        payload: {
          display_name: form.display_name.trim() || null,
          primary_wp_site: form.primary_wp_site || null,
          default_category: form.default_category
            ? Number(form.default_category)
            : null,
          schedule_label: form.schedule_label.trim() || null,
          search_window_hours: form.search_window_hours
            ? Number(form.search_window_hours)
            : null,
          source_url: form.source_url.trim() || null,
          enabled: form.enabled,
          config: {},
        },
      });
      setMsg('Overlay guardado.');
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  return (
    <form className={styles.root} onSubmit={submit}>
      <header className={styles.head}>
        <h2 className={styles.title}>Wiredesk overlay</h2>
        <p className={styles.subtitle}>
          Camada editorial não destrutiva sobre o workflow N8N — Axioma II.
          Nada aqui altera o workflow; usar “Criar override” para mudanças
          que precisem afetar o N8N.
        </p>
      </header>

      {isLoading && <p className={styles.muted}>A carregar overlay…</p>}

      <div className={styles.grid}>
        <label className={styles.field}>
          <span>Display name override</span>
          <input
            value={form.display_name}
            onChange={(e) =>
              setForm({ ...form, display_name: e.target.value })
            }
            placeholder={workflowName}
          />
        </label>

        <label className={styles.field}>
          <span>Site WordPress destino</span>
          <select
            value={form.primary_wp_site}
            onChange={(e) =>
              setForm({ ...form, primary_wp_site: e.target.value })
            }
          >
            <option value="">— sem default —</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.slug})
              </option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          <span>Categoria default (ID WP)</span>
          <input
            type="number"
            min={1}
            value={form.default_category}
            onChange={(e) =>
              setForm({ ...form, default_category: e.target.value })
            }
            placeholder="ex.: 99"
          />
        </label>

        <label className={styles.field}>
          <span>Tempo de busca (horas)</span>
          <input
            type="number"
            min={1}
            max={8760}
            value={form.search_window_hours}
            onChange={(e) =>
              setForm({ ...form, search_window_hours: e.target.value })
            }
            placeholder="24"
          />
        </label>

        <label className={styles.field}>
          <span>Schedule label (informativo)</span>
          <input
            value={form.schedule_label}
            onChange={(e) =>
              setForm({ ...form, schedule_label: e.target.value })
            }
            placeholder="diário 8h"
          />
        </label>

        <label className={styles.field}>
          <span>Source URL (RSS / API override)</span>
          <input
            value={form.source_url}
            onChange={(e) =>
              setForm({ ...form, source_url: e.target.value })
            }
            placeholder="https://news.google.com/rss/..."
          />
        </label>
      </div>

      <label className={styles.checkbox}>
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
        />
        <span>Overlay activo (desligar = ignora todos os campos)</span>
      </label>

      <div className={styles.actions}>
        <Button type="submit" loading={upsertMut.isPending}>
          Guardar overlay
        </Button>
        {msg && <span className={styles.flash}>{msg}</span>}
      </div>
    </form>
  );
}
